import { useEffect } from 'react';

import { resolveChannelDigit } from './channel-keys.js';

/**
 * 숫자 키를 듣는 **얇은 어댑터** (v26, `docs/53` §3.7 · 절대 규칙 71).
 *
 * `use-tv-focus.ts` 와 같은 지위다 — **판정은 순수 함수가 하고 여기는 읽고 부를 뿐이며,
 * 그래서 테스트하지 않는다.**
 *
 * ── 어디서 듣는가 ────────────────────────────────────────────
 *
 * **기본 층에서만.** 시트가 열려 있으면(`[data-focus-scope]` 가 하나라도 있으면) 아무 일도
 * 하지 않는다 — 시청자는 그 층에서 **고르는 중**이고, 층 방침(절대 규칙 32)이 키 하나로
 * 뚫리면 그 규칙이 화살표에만 걸린 셈이 된다.
 *
 * **판정을 DOM 에서 읽는다.** React 상태로 두면 시트가 하나 늘 때마다 이 훅이 그것을
 * 알아야 하고, 그 목록은 조용히 낡는다 — `use-tv-focus.ts` 의 `scopeRoot()` 가 같은
 * 이유로 같은 셀렉터를 본다(절대 규칙 29 의 「하네스의 셀렉터 목록을 두지 말 것」).
 *
 * ── 왜 `clientType` 으로 가르지 않는가 ───────────────────────
 *
 * 포커스 링은 **보이는 것**이라 마우스를 쓰는 화면에서 켜지면 방해가 되지만, 숫자 키는
 * **누르지 않으면 아무 일도 없다** — 켜 두는 대가가 없다. 반대로 `usesKeyboardNav` 로
 * 가르면 **데모가 브라우저 탭이었던 날 조용히 죽고**, 그것은 무대 위에서 처음 안다.
 *
 * `preventDefault` 를 부르지 않는다 — 숫자 키의 기본 동작은 문서형 화면의 아무것도 아니고,
 * 막으면 그때부터 이 훅이 절대 규칙 31 의 검사 밖으로 나간다.
 */
export function useChannelNumberKeys(onPick: ((number: number) => void) | null): void {
  useEffect(() => {
    if (onPick === null) return;

    const onKeyDown = (event: KeyboardEvent) => {
      // 맨 위 층이 기본 층이 아니면 손대지 않는다.
      if (document.querySelector('[data-focus-scope]') !== null) return;

      const target = event.target;
      const editable =
        target instanceof HTMLElement &&
        (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

      const number = resolveChannelDigit({
        key: event.key,
        // 구형 TV 브라우저의 폴백. 표준에서 폐기됐지만 그 기기들이 주는 유일한 값이다.
        keyCode: event.keyCode,
        repeat: event.repeat,
        alt: event.altKey,
        ctrl: event.ctrlKey,
        meta: event.metaKey,
        shift: event.shiftKey,
        editable,
      });
      if (number === null) return;
      onPick(number);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onPick]);
}
